import React, { useState, useEffect, useCallback } from 'react';
import {
  Title2,
  Body1,
  Button,
  Input,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  makeStyles,
  tokens,
  Spinner,
  Card,
} from '@fluentui/react-components';
import {
  FolderRegular,
  DocumentRegular,
  ArrowUploadRegular,
  FolderAddRegular,
  SearchRegular,
  ArrowLeftRegular,
  DeleteRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { getSharePointService } from '../services/sharePointService';


interface DriveItem {
  id: string;
  name: string;
  size?: number;
  lastModifiedDateTime?: string;
  createdDateTime?: string;
  webUrl?: string;
  folder?: any;
  file?: any;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '16px',
    padding: '16px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    flexWrap: 'wrap',
  },
  breadcrumbItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
    color: tokens.colorBrandForeground1,
    ':hover': {
      textDecoration: 'underline',
    },
  },
  breadcrumbSeparator: {
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 1,
    overflow: 'auto',
  },
  departmentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
  },
  departmentCard: {
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
      boxShadow: tokens.shadow4,
      transform: 'translateY(-2px)',
    },
  },
  departmentIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
    marginBottom: '8px',
  },
  departmentName: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  itemsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '12px',
  },
  itemCard: {
    padding: '12px',
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    position: 'relative',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
      boxShadow: tokens.shadow4,
    },
  },
  itemCardDragging: {
    opacity: 0.5,
    cursor: 'grabbing',
  },
  itemCardDragOver: {
    border: `2px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: tokens.shadow8,
  },
  itemIcon: {
    fontSize: '40px',
  },
  folderIcon: {
    color: tokens.colorPaletteYellowBackground2,
  },
  fileIcon: {
    color: tokens.colorBrandForeground1,
  },
  itemName: {
    fontSize: '12px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
    wordBreak: 'break-word',
    width: '100%',
  },
  itemSize: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    color: tokens.colorNeutralForeground3,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
  },
  fileInput: {
    display: 'none',
  },
  errorMessage: {
    padding: '12px',
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
    borderRadius: '4px',
    marginBottom: '16px',
  },
  searchContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  searchInput: {
    flex: 1,
  },
  previewModal: {
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: 'auto',
    height: 'auto',
  },
  previewContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
    maxHeight: '85vh',
    overflow: 'auto',
  },
  previewIframe: {
    width: '100%',
    minHeight: '600px',
    border: 'none',
    borderRadius: '4px',
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '70vh',
    objectFit: 'contain',
    borderRadius: '4px',
    margin: '0 auto',
  },
  previewText: {
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '12px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    maxHeight: '70vh',
    overflow: 'auto',
  },
  previewUnsupported: {
    textAlign: 'center',
    padding: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  dragOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 120, 212, 0.2)',
    border: `3px dashed ${tokens.colorBrandBackground}`,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  dragOverlayContent: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: '32px',
    borderRadius: '8px',
    border: `2px solid ${tokens.colorBrandBackground}`,
    boxShadow: tokens.shadow16,
    textAlign: 'center',
  },
});

const DocumentManagement: React.FC = () => {
  const styles = useStyles();
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFoldersEnsured, setDepartmentFoldersEnsured] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DriveItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [draggedItem, setDraggedItem] = useState<DriveItem | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [isFilePreviewOpen, setIsFilePreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<DriveItem | null>(null);

  const sharePointService = getSharePointService();

  const loadItems = useCallback(async (path: string = '') => {
    try {
      setLoading(true);
      setError('');
      
      // Ensure department folders exist on first load (root level) - only once
      if (path === '' && currentPath.length === 0 && !departmentFoldersEnsured) {
        await sharePointService.ensureDepartmentFoldersExist();
        setDepartmentFoldersEnsured(true);
      }
      
      const driveItems = await sharePointService.getDriveItems(path);
      // Deduplicate items by ID to prevent showing the same folder/file twice
      const uniqueItems = driveItems.reduce((acc: DriveItem[], item: DriveItem) => {
        if (!acc.find(existing => existing.id === item.id)) {
          acc.push(item);
        }
        return acc;
      }, []);
      setItems(uniqueItems);
    } catch (err: any) {
      console.error('Error loading items:', err);
      setError(err.message || 'Failed to load items. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sharePointService, currentPath.length, departmentFoldersEnsured]);

  useEffect(() => {
    const path = currentPath.join('/');
    loadItems(path);
  }, [currentPath, loadItems]);

  const handleFolderClick = (folderName: string) => {
    setCurrentPath([...currentPath, folderName]);
    setSearchTerm('');
  };

  const handleBreadcrumbClick = (index: number) => {
    setCurrentPath(currentPath.slice(0, index + 1));
    setSearchTerm('');
  };

  const handleFileClick = (item: DriveItem) => {
    setPreviewFile(item);
    setIsFilePreviewOpen(true);
  };

  const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return extension;
  };

  const canPreviewInModal = (fileName: string): boolean => {
    const fileType = getFileType(fileName);
    const previewableTypes = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'txt', 'csv'];
    return previewableTypes.includes(fileType);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }

    try {
      setCreatingFolder(true);
      setError('');
      const path = currentPath.join('/');
      await sharePointService.createFolder(path, newFolderName.trim());
      setNewFolderName('');
      setIsCreateFolderDialogOpen(false);
      await loadItems(path);
    } catch (err: any) {
      console.error('Error creating folder:', err);
      setError(err.message || 'Failed to create folder. Please try again.');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setError('');
      const path = currentPath.join('/');
      const fileContent = await selectedFile.arrayBuffer();
      await sharePointService.uploadFile(path, selectedFile.name, fileContent);
      setSelectedFile(null);
      setIsUploadDialogOpen(false);
      // Reset file input
      const fileInput = document.getElementById('file-upload-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      await loadItems(path);
    } catch (err: any) {
      console.error('Error uploading file:', err);
      setError(err.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (item: DriveItem, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent folder click from triggering
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      setDeleting(true);
      setError('');
      await sharePointService.deleteItem(itemToDelete.id);
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      
      // Reload items after deletion
      const path = currentPath.join('/');
      await loadItems(path);
    } catch (err: any) {
      console.error('Error deleting item:', err);
      setError(err.message || 'Failed to delete item. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDragStart = (item: DriveItem, event: React.DragEvent) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.dropEffect = 'move';
    setDraggedItem(item);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverFolder(null);
    setIsDraggingFile(false);
  };

  const handleDragOver = (folderName: string, event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    
    if (draggedItem || isDraggingFile) {
      setDragOverFolder(folderName);
    }
  };

  const handleDragLeave = () => {
    setDragOverFolder(null);
  };

  const handleDrop = async (targetFolderName: string, event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setDragOverFolder(null);
    
    try {
      // Handle file upload from OS
      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        const files = Array.from(event.dataTransfer.files);
        const targetPath = currentPath.length > 0 
          ? [...currentPath, targetFolderName].join('/')
          : targetFolderName;
        
        for (const file of files) {
          try {
            const fileContent = await file.arrayBuffer();
            await sharePointService.uploadFile(targetPath, file.name, fileContent);
            console.log(`✅ Uploaded ${file.name} to ${targetFolderName}`);
          } catch (err: any) {
            console.error(`Error uploading ${file.name}:`, err);
            setError(`Failed to upload ${file.name}: ${err.message}`);
          }
        }
        
        // Reload items after upload
        const path = currentPath.join('/');
        await loadItems(path);
        setIsDraggingFile(false);
        return;
      }
      
      // Handle moving existing item
      if (draggedItem) {
        const targetPath = currentPath.length > 0 
          ? [...currentPath, targetFolderName].join('/')
          : targetFolderName;
        
        const sourcePath = currentPath.join('/');
        
        await sharePointService.moveItem(draggedItem.id, sourcePath, targetPath, draggedItem.name);
        console.log(`✅ Moved ${draggedItem.name} to ${targetFolderName}`);
        
        // Reload items after move
        await loadItems(sourcePath);
        setDraggedItem(null);
      }
    } catch (err: any) {
      console.error('Error handling drop:', err);
      setError(err.message || 'Failed to move/upload item. Please try again.');
      setDraggedItem(null);
      setIsDraggingFile(false);
    }
  };

  const handleDropOnContainer = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    setIsDraggingFile(false);
    
    // Handle file upload to current folder
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const files = Array.from(event.dataTransfer.files);
      const targetPath = currentPath.join('/');
      
      try {
        setUploading(true);
        setError('');
        
        for (const file of files) {
          try {
            const fileContent = await file.arrayBuffer();
            await sharePointService.uploadFile(targetPath, file.name, fileContent);
            console.log(`✅ Uploaded ${file.name}`);
          } catch (err: any) {
            console.error(`Error uploading ${file.name}:`, err);
            setError(`Failed to upload ${file.name}: ${err.message}`);
          }
        }
        
        await loadItems(targetPath);
      } catch (err: any) {
        console.error('Error uploading files:', err);
        setError(err.message || 'Failed to upload files. Please try again.');
      } finally {
        setUploading(false);
      }
    }
  };

  const handleContainerDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Check if dragging files from OS
    if (event.dataTransfer.types.includes('Files')) {
      event.dataTransfer.dropEffect = 'copy';
      setIsDraggingFile(true);
    }
  };

  const handleContainerDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Check if dragging files from OS
    if (event.dataTransfer.types.includes('Files')) {
      setIsDraggingFile(true);
    }
  };

  const handleContainerDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Only hide drag overlay if we're leaving the container itself, not child elements
    if (event.currentTarget === event.target) {
      setIsDraggingFile(false);
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const folders = filteredItems.filter(item => item.folder);
  const files = filteredItems.filter(item => item.file);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Document Management</Title2>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            icon={<FolderAddRegular />}
            onClick={() => setIsCreateFolderDialogOpen(true)}
          >
            New Folder
          </Button>
          <Button
            appearance="primary"
            icon={<ArrowUploadRegular />}
            onClick={() => setIsUploadDialogOpen(true)}
          >
            Upload File
          </Button>
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      {/* Drag and Drop Overlay */}
      {isDraggingFile && (
        <div className={styles.dragOverlay}>
          <div className={styles.dragOverlayContent}>
            <ArrowUploadRegular style={{ fontSize: '48px', color: tokens.colorBrandForeground1, marginBottom: '16px' }} />
            <Title2>Drop files here to upload</Title2>
            <Body1>
              {currentPath.length > 0 
                ? `Files will be uploaded to: ${currentPath.join(' / ')}`
                : 'Files will be uploaded to the root folder'}
            </Body1>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      {currentPath.length > 0 && (
        <div className={styles.breadcrumb}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => setCurrentPath([])}
            style={{ marginRight: '8px' }}
          >
            Back to Documents
          </Button>
          <div
            className={styles.breadcrumbItem}
            onClick={() => handleBreadcrumbClick(-1)}
          >
            <FolderRegular /> Documents
          </div>
          {currentPath.map((segment, index) => (
            <React.Fragment key={index}>
              <span className={styles.breadcrumbSeparator}>/</span>
              <div
                className={styles.breadcrumbItem}
                onClick={() => handleBreadcrumbClick(index)}
              >
                {segment}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Search */}
      {items.length > 0 && (
        <div className={styles.searchContainer}>
          <Input
            className={styles.searchInput}
            placeholder="Search files and folders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            contentBefore={<SearchRegular />}
          />
        </div>
      )}

      <div 
        className={styles.content}
        onDrop={handleDropOnContainer}
        onDragOver={handleContainerDragOver}
        onDragEnter={handleContainerDragEnter}
        onDragLeave={handleContainerDragLeave}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Spinner size="large" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className={styles.emptyState}>
            <FolderRegular style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.3 }} />
            <Body1>This folder is empty</Body1>
            {searchTerm && <Body1>No items match your search</Body1>}
          </div>
        ) : (
          // Show all items (folders and files)
          <div className={styles.itemsGrid}>
            {folders.map((item) => (
              <Card
                key={item.id}
                className={`${styles.itemCard} ${draggedItem?.id === item.id ? styles.itemCardDragging : ''} ${dragOverFolder === item.name ? styles.itemCardDragOver : ''}`}
                onClick={() => handleFolderClick(item.name)}
                draggable={true}
                onDragStart={(e) => handleDragStart(item, e)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(item.name, e)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(item.name, e)}
              >
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={(e) => handleDeleteClick(item, e)}
                  title="Delete folder"
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    padding: '4px',
                    minWidth: 'auto',
                    width: '24px',
                    height: '24px',
                    backgroundColor: tokens.colorPaletteRedBackground2,
                    color: tokens.colorPaletteRedForeground2,
                    borderRadius: '4px',
                  }}
                />
                <FolderRegular 
                  className={styles.itemIcon}
                  style={{ color: tokens.colorBrandForeground1 }}
                />
                <div className={styles.itemName}>{item.name}</div>
                {item.lastModifiedDateTime && (
                  <div className={styles.itemSize}>
                    Modified: {formatDate(item.lastModifiedDateTime)}
                  </div>
                )}
              </Card>
            ))}
            {files.map((item) => (
              <Card
                key={item.id}
                className={`${styles.itemCard} ${draggedItem?.id === item.id ? styles.itemCardDragging : ''}`}
                onClick={() => handleFileClick(item)}
                draggable={true}
                onDragStart={(e) => handleDragStart(item, e)}
                onDragEnd={handleDragEnd}
              >
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={(e) => handleDeleteClick(item, e)}
                  title="Delete file"
                  style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    padding: '4px',
                    minWidth: 'auto',
                    width: '24px',
                    height: '24px',
                    backgroundColor: tokens.colorPaletteRedBackground2,
                    color: tokens.colorPaletteRedForeground2,
                    borderRadius: '4px',
                  }}
                />
                <DocumentRegular 
                  className={styles.itemIcon}
                  style={{ color: tokens.colorBrandForeground1 }}
                />
                <div className={styles.itemName}>{item.name}</div>
                <div className={styles.itemSize}>
                  {formatFileSize(item.size)}
                  {item.lastModifiedDateTime && ` • ${formatDate(item.lastModifiedDateTime)}`}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Folder Dialog */}
      <Dialog open={isCreateFolderDialogOpen} onOpenChange={(_, data) => setIsCreateFolderDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Input
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setIsCreateFolderDialogOpen(false);
                  setNewFolderName('');
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleCreateFolder}
                disabled={creatingFolder || !newFolderName.trim()}
              >
                {creatingFolder ? <Spinner size="tiny" /> : 'Create'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Upload File Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={(_, data) => setIsUploadDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Upload File</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <input
                id="file-upload-input"
                type="file"
                className={styles.fileInput}
                onChange={handleFileSelect}
              />
              <Button
                appearance="outline"
                icon={<ArrowUploadRegular />}
                onClick={() => document.getElementById('file-upload-input')?.click()}
              >
                Select File
              </Button>
              {selectedFile && (
                <Body1>Selected: {selectedFile.name}</Body1>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setIsUploadDialogOpen(false);
                  setSelectedFile(null);
                  const fileInput = document.getElementById('file-upload-input') as HTMLInputElement;
                  if (fileInput) {
                    fileInput.value = '';
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
              >
                {uploading ? <Spinner size="tiny" /> : 'Upload'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={(_, data) => setIsDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              Delete {itemToDelete?.folder ? 'Folder' : 'File'}?
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Body1>
                Are you sure you want to delete <strong>{itemToDelete?.name}</strong>?
                {itemToDelete?.folder && (
                  <span style={{ display: 'block', marginTop: '8px', color: tokens.colorPaletteRedForeground2 }}>
                    Warning: This will delete the folder and all its contents permanently.
                  </span>
                )}
              </Body1>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setItemToDelete(null);
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                style={{ backgroundColor: tokens.colorPaletteRedBackground2, color: tokens.colorPaletteRedForeground2 }}
              >
                {deleting ? <Spinner size="tiny" /> : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* File Preview Modal */}
      <Dialog open={isFilePreviewOpen} onOpenChange={(_, data) => setIsFilePreviewOpen(data.open)}>
        <DialogSurface className={styles.previewModal}>
          <DialogBody>
            <DialogTitle>
              {previewFile?.name || 'File Preview'}
            </DialogTitle>
            <DialogContent className={styles.previewContent}>
              {previewFile && (
                <>
                  {canPreviewInModal(previewFile.name) ? (
                    <>
                      {getFileType(previewFile.name) === 'pdf' ? (
                        <iframe
                          src={previewFile.webUrl}
                          className={styles.previewIframe}
                          title={previewFile.name}
                        />
                      ) : ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(getFileType(previewFile.name)) ? (
                        <img
                          src={previewFile.webUrl}
                          alt={previewFile.name}
                          className={styles.previewImage}
                        />
                      ) : ['txt', 'csv'].includes(getFileType(previewFile.name)) ? (
                        <div className={styles.previewText}>
                          <Spinner size="small" label="Loading file content..." />
                          <Body1>Text preview coming soon. Please download to view.</Body1>
                        </div>
                      ) : (
                        <div className={styles.previewUnsupported}>
                          <DocumentRegular style={{ fontSize: '48px', opacity: 0.5 }} />
                          <Body1>Preview not available for this file type.</Body1>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.previewUnsupported}>
                      <DocumentRegular style={{ fontSize: '48px', opacity: 0.5 }} />
                      <Body1>Preview not available for this file type.</Body1>
                      <Body1>File type: .{getFileType(previewFile.name)}</Body1>
                    </div>
                  )}
                  
                  {previewFile.webUrl && (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <Button
                        appearance="primary"
                        icon={<ArrowDownloadRegular />}
                        onClick={() => {
                          if (previewFile.webUrl) {
                            window.open(previewFile.webUrl, '_blank');
                          }
                        }}
                      >
                        Open in SharePoint
                      </Button>
                      <Button
                        appearance="secondary"
                        onClick={async () => {
                          if (previewFile) {
                            try {
                              const blob = await sharePointService.downloadFile(previewFile.id);
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = previewFile.name;
                              document.body.appendChild(a);
                              a.click();
                              window.URL.revokeObjectURL(url);
                              document.body.removeChild(a);
                            } catch (err) {
                              console.error('Error downloading file:', err);
                              setError('Failed to download file. Please try opening in SharePoint.');
                            }
                          }
                        }}
                      >
                        Download
                      </Button>
                    </div>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setIsFilePreviewOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default DocumentManagement;

